import { Form, Formik, FormikHelpers } from 'formik';
import * as lodash from 'lodash';
import { OrderingSelectors } from 'polygon-ordering';
import { RedcatApiHandler } from 'polygon-utils';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import StandardButton from '../components/StandardButton';
import Text from '../components/Text';
import TouchableOpacity from '../components/TouchableOpacity';
import getMemberFields from '../selectors/getMemberFields';
import getMemberSchema from '../selectors/getMemberSchema';
import getProfileInitialValues from '../selectors/getProfileInitialValues';
import { syncMember } from '../thunks/syncMember';
import {
  enqueueErrorSnackbar,
  enqueueSuccessSnackbar,
  enqueueWarningSnackbar,
} from '../utils/snackbar';
import ProfileFields from './ProfileFields';
import MFACodeInput from '../components/MFACodeInput';
import combineStyles from '../utils/combineStyles';
import { TEXT_PROPERTIES, CONTAINER_PROPERTIES } from '../utils/theme';
import getThemeLookup from '../selectors/getThemeLookup';
import LoyaltyTitle from '../components/LoyaltyTitle';
import { MdKeyboardArrowLeft } from 'react-icons/md';
import ScreenLink from '../components/ScreenLink';
import { alertUnsavedChanges } from '../utils/alertUnsavedChanges';
import { AUTH_MODE } from '../constants/authMode';

export const PROFILE_ROUTE = '/profile';

const { getMember } = OrderingSelectors;

const ProfileScreen = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const initialVal = useAppSelector(getProfileInitialValues);
  const memberFields = useAppSelector(getMemberFields);
  const memberSchema = useAppSelector(getMemberSchema);
  const [mfaToken, setMfaToken] = useState(null);
  const [resendLinkClicked, setResetLinkClicked] = useState(false);
  const [resendLinkApiReq, setResendLinkApiReq] = useState(false);
  const [invalidPin, setInvalidPin] = useState(false);
  const [timer, setTimer] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(60);
  const [error, setError] = useState<any>({});
  const [formikInitialValues, setFormikInitialValues] = useState<RegisterFields[]>([]);
  const member = useAppSelector(getMember);
  const p = useAppSelector(getThemeLookup);
  const twoFactorAuthMode = useAppSelector(state => state.config.twoFactorAuthMode);
  const [verifyingNewEmailMobile, setVerifyingNewEmailMobile] = useState(false);
  // had to seperate this because the or condition was not working as expected and the smsCondition becomes messy with too many ands and ors
  const smsConditionBasedOnResponse = Object.keys(error).length === 0 || error?.error_code === 3;
  // There is a global setting for TFA but given the user has the option to pick we want to show that accordingly
  // if global tfa condition is sms and user has sms as tfa then show sms messaging
  // if global tfa condition is email and user has sms as tfa then show sms messaging
  // if error code is 5 then show sms messaging
  const smsMessaging =
    (twoFactorAuthMode === AUTH_MODE.SMS &&
      member?.tfaAuthMode === AUTH_MODE.SMS &&
      smsConditionBasedOnResponse) ||
    (twoFactorAuthMode === AUTH_MODE.EMAIL &&
      member?.tfaAuthMode === AUTH_MODE.SMS &&
      smsConditionBasedOnResponse) ||
    error?.error_code === 5;
  const mfaVerifyTitle = smsMessaging
    ? t('LoyaltyWrapper.verifySms', {
        new: error?.error_code === 5 ? 'new' : '',
      })
    : t('LoyaltyWrapper.verifyEmail', {
        new: error?.error_code === 4 ? 'new' : '',
      });
  const resendLinkText = smsMessaging ? t('link.resendSms.action') : t('link.resendEmail.action');
  const resendLinkCountdownText = smsMessaging
    ? `${t('LoyaltyWrapper.resendSms')} ${t('subString.in')} ${timeLeft}s`
    : `${t('LoyaltyWrapper.resendEmail')} ${t('subString.in')} ${timeLeft}s`;
  const mfaVerifySubText = smsMessaging
    ? t('LoyaltyWrapper.mfaCodeSmsText', {
        new: error?.error_code === 5 ? 'new' : '',
      })
    : t('LoyaltyWrapper.mfaCodeEmailText', {
        new: error?.error_code === 4 ? 'new' : '',
      });

  const setFieldValueRef = useRef<((field: string, value: any) => void) | null>(null);

  useEffect(() => {
    if (timer !== null) {
      const interval = setInterval(() => {
        setTimeLeft(prevTime => {
          if (prevTime <= 1) {
            clearInterval(interval);
            setResetLinkClicked(false);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [timer]);

  // Handle MFA token setting for error codes 4 and 5
  useEffect(() => {
    setVerifyingNewEmailMobile(error?.error_code === 4 || error?.error_code === 5);
    if (setFieldValueRef.current && (error?.error_code === 4 || error?.error_code === 5)) {
      if (error?.error.includes('new mobile')) {
        setFieldValueRef.current('TFATokenNewMobile', error?.additional_info);
      }
      if (error?.error.includes('new email')) {
        setFieldValueRef.current('TFATokenNewEmail', error?.additional_info);
      }
    }
  }, [error]);

  const submit = (values: Partial<RegisterFields>, formikHelpers: FormikHelpers<FormikFields>) => {
    const { setSubmitting, resetForm } = formikHelpers;
    const path = '/api/v1/profile';

    // Omit the 'Terms' field if it exists
    const sanitizedValues: any = lodash.omit(values, 'Terms');

    // Loop through each key in the sanitized values and remove it if the key
    // does not exist in the editableFields array.
    // Object.keys(sanitizedValues).forEach(key => {
    //   if (!editableFields.some(field => field.id === key)) {
    //     delete sanitizedValues[key];
    //   }
    // });

    const constructUdpateProfilePacket =
      mfaToken && !resendLinkApiReq
        ? //@ts-ignore
          {
            ...lodash.omit(sanitizedValues, [
              'mfaCode',
              'TFAPinNewMobile',
              'TFAPinNewEmail',
              'TFATokenNewMobile',
              'TFATokenNewEmail',
            ]),
            tfa_pin: sanitizedValues.mfaCode,
            tfa_token: mfaToken,
          }
        : lodash.omit(sanitizedValues, [
            'mfaCode',
            'TFAPinNewMobile',
            'TFAPinNewEmail',
            'TFATokenNewMobile',
            'TFATokenNewEmail',
          ]);
    // we need to handle scanerios where we users enter incorrect tokens, update it then retry
    const phase2Packet =
      invalidPin || resendLinkApiReq
        ? lodash.omit(sanitizedValues, ['mfaCode', 'TFAPinNewMobile', 'TFAPinNewEmail']) // phase 2 retry where we still want to have 'TFATokenNewMobile' or 'TFATokenNewEmail'
        : lodash.omit(sanitizedValues, ['mfaCode']); // successful phase 2 packet
    RedcatApiHandler.authorisedFetch({
      path,
      method: 'PUT',
      body: {
        ...(verifyingNewEmailMobile ? phase2Packet : constructUdpateProfilePacket),
      },
    })
      .then((response: { token: any; success: any }) => {
        setMfaToken(null);

        if (response.token) {
          dispatch(syncMember());
        }
        if (response.success) {
          enqueueSuccessSnackbar(t('updateSuccess'));
          setSubmitting(false);
          resetForm({ values });
        }
      })
      .catch((err: any) => {
        setSubmitting(false);
        const data = lodash.get(err, 'details.json', {});
        if (data.error_code === 3) {
          setMfaToken(data.additional_info);
          setError(data);
          enqueueWarningSnackbar(data.error);
          return;
        } else if (data.error_code === 4) {
          setResetLinkClicked(false);
          setMfaToken(data.additional_info);
          setError(data);
          enqueueWarningSnackbar(data.error);
          return;
        } else if (data.error_code === 5) {
          setResetLinkClicked(false);
          setMfaToken(data.additional_info);
          setError(data);
          enqueueWarningSnackbar(data.error);
          return;
        } else {
          setInvalidPin(true);
          enqueueErrorSnackbar(err);
        }
      });

    setResendLinkApiReq(false);
    if (invalidPin) {
      setInvalidPin(false);
    }
  };

  // hide fields based on mfaToken
  const inputFields = (
    mfaToken
      ? memberFields?.visible.map((field: FormikFields) => ({
          ...field,
          editMode: 'hidden',
        }))
      : memberFields?.visible
  ) as FormikFields[];

  const filteredinputFields = inputFields.filter(
    (field: FormikFields) =>
      field.inputType !== 'sendemail' && field.inputType !== 'sendsms' && field.id !== 'Terms',
  );

  const communicationinputFields = inputFields.filter(
    (field: FormikFields) => field.inputType === 'sendemail' || field.inputType === 'sendsms',
  );

  const initVals = {
    ...initialVal.visible,
    mfaCode: '',
    ...(Object.prototype.hasOwnProperty.call(initialVal.visible, 'TFAAuthMode') && {
      TFAAuthMode: String(initialVal.visible.TFAAuthMode),
    }),
  };

  const hasChanges = (currentValues: any) => {
    const initalValues = {
      ...initVals,
      Terms: true,
    };

    // Filter out mfaCode since it's not part of the comparison
    return Object.keys(currentValues).some(key => {
      // Handle empty string vs undefined case
      const currentVal = currentValues[key] || '';
      const initialVal = initalValues[key as keyof typeof initalValues] || '';
      return currentVal !== initialVal;
    });
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ marginTop: 50 }}>
        <LoyaltyTitle title={mfaToken ? mfaVerifyTitle : t('LoyaltyWrapper.profilePageTitle')} />
      </div>
      {mfaToken && (
        <TouchableOpacity
          onClick={() => {
            setMfaToken(null);
            setError({});
          }}
          style={{ position: 'absolute', top: 5, left: -60 }}
        >
          <MdKeyboardArrowLeft
            style={{
              fontSize: 28,
            }}
          />
        </TouchableOpacity>
      )}
      <Formik
        validationSchema={
          // bypass validation if resendLinkClicked is true
          mfaToken && !verifyingNewEmailMobile && !resendLinkClicked
            ? memberSchema.visible?.shape({
                mfaCode: Yup.string().required('Required').min(4, 'Too Short'),
              })
            : memberSchema.visible
        }
        initialValues={initVals}
        //@ts-ignore
        onSubmit={submit}
        enableReinitialize={true}
      >
        {({ handleSubmit, isSubmitting, values, isValid, dirty, setFieldValue }) => {
          // we want terms to be hidden but also make sure it passes the validation
          // I have disabled linting for this useEffect because we will likely
          // refactor out Formik in the future and this is the simplest solution for now
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            if (Object.prototype.hasOwnProperty.call(values, 'Terms')) {
              setFieldValue('Terms', true);
            }
          }, []);

          // Store setFieldValue reference for the useEffect that handles MFA token setting
          setFieldValueRef.current = setFieldValue;

          const tiggerSubmit = () => {
            // Simply call handleSubmit directly - Terms field handling moved to submit function
            handleSubmit();
          };

          const handleResendLinkClickedClick = async () => {
            setResetLinkClicked(true);
            setTimer(Date.now());
            setTimeLeft(60);
            setResendLinkApiReq(true);
            // Somehow this helps with ensure the handleSubmit
            await new Promise(resolve => setTimeout(resolve, 0));
            handleSubmit();
          };

          // Store initial values only once to avoid re-renders
          if (formikInitialValues.length === 0 && Object.keys(values).length > 0) {
            // Use setTimeout to avoid state update during render
            setTimeout(() => {
              setFormikInitialValues(values as unknown as RegisterFields[]);
            }, 0);
          }

          alertUnsavedChanges(
            t('LoyaltyWrapper.unsavedChanges'),
            hasChanges(values),
            tiggerSubmit,
            Boolean(mfaToken),
          );

          const setMfaValues = async (mfaCodeValues: string[]) => {
            const mfaCode = mfaCodeValues.join(''); // Combine the array into a single string
            if (verifyingNewEmailMobile) {
              if (error.error.includes('new mobile')) {
                setFieldValue('TFAPinNewMobile', mfaCode);
              }
              if (error.error.includes('new email')) {
                setFieldValue('TFAPinNewEmail', mfaCode);
              }
            } else {
              setFieldValue('mfaCode', mfaCode);
            }
          };

          return (
            <Form onSubmit={handleSubmit}>
              {filteredinputFields.map((field: FormikFields) => (
                <ProfileFields
                  field={field}
                  key={field.id}
                  defaultValue={field.id === 'Email' ? member?.email : ''}
                />
              ))}
              {Boolean(mfaToken) && (
                <div style={{ width: '100%', maxWidth: '800px', margin: '0 auto' }}>
                  <div style={{ marginBottom: '30px', textAlign: 'center' }}>
                    <Text value={mfaVerifySubText} style={{ fontSize: 14 }} />
                  </div>
                  <MFACodeInput
                    placeholder=" "
                    inputStyle={combineStyles(
                      styles.mfaInputStyle,
                      p('mfaCodeInput', [...TEXT_PROPERTIES, 'border']),
                    )}
                    containerStyle={combineStyles(
                      styles.mfaInputContainerStyle,
                      p('mfaContainerStyle', [...CONTAINER_PROPERTIES]),
                    )}
                    onChange={setMfaValues}
                    resetMFAInput={verifyingNewEmailMobile}
                  />
                </div>
              )}
              {!mfaToken && (
                <div className="position-relative" style={{ marginTop: 20 }}>
                  <StandardButton
                    containerStyle={styles.button}
                    themeKey="loyaltyButton"
                    label={t('button.updateProfile')}
                    onClick={handleSubmit}
                    disabled={
                      isSubmitting ||
                      (!isValid && !verifyingNewEmailMobile) ||
                      !dirty ||
                      !hasChanges(values)
                    }
                    showSpinner={isSubmitting}
                  />
                </div>
              )}
              {!mfaToken && (
                <>
                  <div style={{ marginTop: '50px', marginBottom: '20px' }}>
                    <Text
                      value={t('LoyaltyWrapper.communicationHeading')}
                      themeKey="communicationHeading"
                    />
                  </div>
                  <div>
                    <Text
                      value={t('LoyaltyWrapper.communicationSubHeading')}
                      themeKey="communicationSubHeading"
                    />
                  </div>
                </>
              )}
              {communicationinputFields.map((field: FormikFields) => (
                <ProfileFields
                  field={field}
                  key={field.id}
                  defaultValue={field.id === 'Email' ? member?.email : ''}
                />
              ))}
              {!mfaToken && (
                <div className="position-relative" style={{ marginTop: 20 }}>
                  <StandardButton
                    containerStyle={styles.button}
                    themeKey="loyaltyButton"
                    label={t('button.updatePreferences')}
                    onClick={tiggerSubmit}
                    disabled={
                      isSubmitting ||
                      // @ts-ignore
                      (values['SendEmail'] === formikInitialValues['SendEmail'] &&
                        // @ts-ignore
                        values['SendSMS'] === formikInitialValues['SendSMS'])
                    }
                    showSpinner={isSubmitting}
                  />
                </div>
              )}
              {mfaToken && (
                <div style={{ width: '100%', maxWidth: '800px', margin: '0 auto' }}>
                  <div className="position-relative" style={styles.verifyButtonLink}>
                    <StandardButton
                      containerStyle={{ width: '100%', maxWidth: '400px' }}
                      themeKey="loyaltyButton"
                      label={t('button.verify')}
                      onClick={handleSubmit}
                      disabled={isSubmitting || !isValid || !dirty || !values.mfaCode.length}
                      showSpinner={isSubmitting}
                    />
                  </div>
                  <div className="position-relative" style={styles.verifyButtonLink}>
                    <ScreenLink
                      label={''}
                      actionLabel={
                        resendLinkClicked && timeLeft > 0 ? resendLinkCountdownText : resendLinkText
                      }
                      url={'#'}
                      onClick={resendLinkClicked ? () => {} : handleResendLinkClickedClick}
                      themeKeyLabel={
                        resendLinkClicked ? 'resendCodeTextDisabled' : 'resendCodeText'
                      }
                      themeKeyActionLabel={
                        resendLinkClicked ? 'resendCodeTextDisabled' : 'resendCodeText'
                      }
                    />
                  </div>
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

const styles: Styles = {
  button: {
    borderRadius: 5,
    height: 40,
  },
  mfaInputStyle: {
    margin: 0,
    //we need this in order for mobile to show the inputs users have entered
    color: 'inherit',
    textAlign: 'center',
    padding: 0,
  },
  mfaInputContainerStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 50,
    width: '100%',
  },
  verifyButtonLink: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
};

export default ProfileScreen;
